/**
 * CartDrawerFooter - 购物车底部容器组件
 * 
 * 职责：
 * - 组合三个子区域：信息区、结账区、功能区
 * - 处理空购物车场景
 * - 提供统一的布局和间距
 * 
 * 架构：
 * - 容器组件可直接使用 Hook/Store
 * - 子组件通过 Props 接收数据
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CartInfoPanel } from './CartInfoPanel';
import { CheckoutPanel } from './CheckoutPanel';
import { ActionPanel } from './ActionPanel';

import { useCartInfo } from '@/hooks/cart/useCartInfo';
import { useCartFooter } from '@/hooks/cart/useCartFooter';
import { t } from '@/constants/i18n';

interface CartDrawerFooterProps {
    /** 设置抽屉开关状态的方法 */
    setIsOpen: (isOpen: boolean) => void;
    /** 切换折扣码面板开关的方法 */
    toggleDiscountPanel: (isOpen?: boolean) => void;
}

/**
 * 空购物车 Footer
 */
export const EmptyCartFooter = ({ setIsOpen }: { setIsOpen: (isOpen: boolean) => void }) => (
    <>
        <Separator />
        <div className="p-0 bg-background">
            <Button onClick={() => setIsOpen(false)} className="w-full" size="lg">
                {t('startShopping')}
            </Button>
        </div>
    </>
);

/**
 * 购物车 Footer（三段式）
 */
export const CartDrawerFooter: React.FC<CartDrawerFooterProps> = ({
    setIsOpen,
    toggleDiscountPanel,
}) => {
    const {
        cartSyncStatus,
        total_price,
        original_total_price,
        currency,
        isEmpty
    } = useCartFooter();

    // 空购物车场景
    if (isEmpty) {
        return <EmptyCartFooter setIsOpen={setIsOpen} />;
    }

    // 使用 Hook 计算信息提示
    const cartInfo = useCartInfo();
    return (
        <>
            <Separator />
            <div className="p-0 bg-background space-y-4">
                {/* 1. 信息展示区 */}
                <CartInfoPanel result={cartInfo} currency={currency} />

                {/* 2. 结账操作区 */}
                <CheckoutPanel
                    totalPriceCents={total_price}
                    originalTotalPriceCents={original_total_price}
                    cartSyncStatus={cartSyncStatus}
                />

                {/* 3. 功能入口区 */}
                <ActionPanel
                    onDiscountClick={() => toggleDiscountPanel(true)}
                    onCartDetailClick={() => (window.location.href = '/cart')}
                    showDiscount={true}
                    showCartDetail={true}
                />
            </div>
        </>
    );
};
